import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGES, STAGE_COLORS, fmtMoney } from "@/lib/constants";
import { ScoreBadge } from "@/components/RiskDot";
import LeadFormDialog from "@/components/LeadFormDialog";
import DocViewer from "@/components/DocViewer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DotsSixVertical, Paperclip, Eye, File as FileIcon, DownloadSimple } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export default function Pipeline() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [docsLead, setDocsLead] = useState(null);
  const [leadDocs, setLeadDocs] = useState([]);
  const [viewFile, setViewFile] = useState(null);
  const canEdit = hasRole("vanzari", "pm");

  const openDocs = async (e, lead) => {
    e.stopPropagation();
    setDocsLead(lead);
    try { const { data } = await api.get(`/leads/${lead.id}/documents`); setLeadDocs(data || []); }
    catch { setLeadDocs([]); }
  };

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const moveStage = async (id, stage) => {
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.stage === stage) return;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, stage } : l)));
    try { await api.put(`/leads/${id}`, { stage }); }
    catch { toast.error("Eroare la actualizare"); load(); }
  };

  const convert = async (e, id) => {
    e.stopPropagation();
    const code = window.prompt("Cod proiect (obligatoriu, ex: HGS 1904):", "");
    if (code === null) return;
    if (!code.trim()) return toast.error("Codul proiectului este obligatoriu");
    try {
      const { data } = await api.post(`/leads/${id}/convert`, { code: code.trim() });
      toast.success("Lead convertit în proiect");
      navigate(`/projects/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Conversia a eșuat"); }
  };

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-8 fade-up" data-testid="pipeline-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">CRM</p>
          <h1 className="font-head text-4xl font-extrabold tracking-tighter">Pipeline</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gestionează oportunitățile de vânzare</p>
        </div>
        {canEdit && <LeadFormDialog onCreated={(l) => setLeads((ls) => [l, ...ls])} triggerLabel="Oportunitate Nouă" triggerTestId="add-opp-btn" />}
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {STAGES.map((s) => {
          const items = leads.filter((l) => l.stage === s.value);
          const sum = items.reduce((a, l) => a + (Number(l.estimated_value) || 0), 0);
          const sc = STAGE_COLORS[s.value] || STAGE_COLORS.nou;
          return (
            <div key={s.value} className="w-80 shrink-0"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (canEdit && dragId) moveStage(dragId, s.value); setDragId(null); }}
              data-testid={`kanban-col-${s.value}`}>
              <div className={cn("mb-3 flex items-center justify-between border-b-2 pb-2", sc.border)}>
                <span className={cn("text-sm font-extrabold uppercase tracking-wide", sc.text)}>{s.label}</span>
                <span className="rounded-md bg-white/5 px-2 py-0.5 text-xs text-muted-foreground">{items.length}</span>
              </div>
              <div className="mb-3 text-lg font-bold tabular-nums text-muted-foreground">{fmtMoney(sum)}</div>
              <div className="min-h-[120px] space-y-2.5">
                {items.map((l) => (
                  <div key={l.id} draggable={canEdit} onDragStart={() => setDragId(l.id)}
                    onClick={() => navigate(`/leads/${l.id}`)}
                    className="group cursor-pointer rounded-xl border border-border bg-card p-4 transition-colors hover:border-gray-600"
                    data-testid={`lead-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-bold leading-tight">{l.company}</div>
                      {canEdit && <DotsSixVertical size={16} className="mt-0.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{l.owner_name || "Nealocat"}</span>
                      {l.doc_count > 0 && <button onClick={(e) => openDocs(e, l)} className="inline-flex items-center gap-0.5 rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-blue-300 hover:bg-blue-500/30" title="Vezi documentele leadului" data-testid={`lead-docs-btn-${l.id}`}><Paperclip size={10} /> {l.doc_count}</button>}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-bold tabular-nums text-emerald-400">{fmtMoney(l.estimated_value)}</span>
                      <ScoreBadge score={l.ai_score} color={l.score_color} />
                    </div>
                    {l.product_type && <div className="mt-2 truncate text-xs text-muted-foreground">{l.product_type}</div>}
                    {s.value === "castigat" && canEdit && !l.converted_project_id && (
                      <button onClick={(e) => convert(e, l.id)} data-testid={`convert-lead-${l.id}`}
                        className="mt-3 w-full rounded-lg bg-emerald-500/15 py-1.5 text-xs font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/25">
                        Convertește în proiect
                      </button>
                    )}
                  </div>
                ))}
                {items.length === 0 && <div className="rounded-xl border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground/60">Nicio oportunitate</div>}
              </div>
            </div>
          );
        })}
      </div>
      <Dialog open={!!docsLead} onOpenChange={(o) => !o && setDocsLead(null)}>
        <DialogContent data-testid="pipeline-lead-docs">
          <DialogHeader><DialogTitle className="truncate pr-8">Documente — {docsLead?.company}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {leadDocs.map((f) => (
              <div key={f.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid={`pipeline-doc-${f.id}`}>
                <FileIcon size={16} className="shrink-0 text-blue-400" />
                <span className="min-w-0 flex-1 truncate text-sm">{f.name}</span>
                <button onClick={() => setViewFile(f)} className="text-muted-foreground hover:text-primary" title="Vizualizează" data-testid={`pipeline-doc-view-${f.id}`}><Eye size={16} /></button>
                <a href={fileUrl(f.storage_path)} download={f.name} className="text-muted-foreground hover:text-foreground"><DownloadSimple size={16} /></a>
              </div>
            ))}
            {leadDocs.length === 0 && <p className="text-sm text-muted-foreground">Niciun document pe acest lead.</p>}
          </div>
        </DialogContent>
      </Dialog>
      <DocViewer file={viewFile} onClose={() => setViewFile(null)} />
    </div>
  );
}
